import React, { useState } from "react";
import Layout from '../components/Layout';
import "./BillingDocsDashboard.css";
import {
  BookOpen,
  Code,
  PlugsConnected,
  Check,
  Warning,
  Clock,
  FileText,
  Download,
  Copy,
  CheckCircle,
  Money,
  Lock,
  Envelope,
  Prohibit,
  ChartBar,
  ArrowsClockwise,
  Lightbulb,
  Question,
  ChatCircle,
  DeviceMobile,
  CreditCard,
  Phone
} from '@phosphor-icons/react';

/**
 * Billing Documentation Dashboard
 * comprehensive guide for platform subscription billing
 */
const BillingDocsDashboard = () => {
  const [activeSection, setActiveSection] = useState("overview");
  const [copiedEndpoint, setCopiedEndpoint] = useState(null);

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedEndpoint(id);
    setTimeout(() => setCopiedEndpoint(null), 2000);
  };

  const pageContent = (
    <div className="page">
      {/* Header */}
      <div className="docs-header">
        <div>
          <h1>Billing Documentation</h1>
          <p>Complete guide to platform subscription billing, payment processing, and API integration</p>
        </div>
      </div>

      {/* Navigation Sidebar */}
      <div className="docs-container">
        <div className="docs-sidebar">
          <nav className="docs-nav">
            <div className="nav-section">
              <h5>Getting Started</h5>
              <button
                className={`nav-item ${activeSection === "overview" ? "active" : ""}`}
                onClick={() => setActiveSection("overview")}
              >
                <BookOpen size={16} className="me-2" />
                Overview
              </button>
             
            </div>

            <div className="nav-section">
              <h5>Reference</h5>
              <button
                className={`nav-item ${activeSection === "features" ? "active" : ""}`}
                onClick={() => setActiveSection("features")}
              >
                <Check size={16} className="me-2" />
                Features
              </button>
            </div>

            <div className="nav-section">
              <h5>Advanced</h5>
              <button
                className={`nav-item ${activeSection === "late-fees" ? "active" : ""}`}
                onClick={() => setActiveSection("late-fees")}
              >
                <Warning size={16} className="me-2" />
                Late Fees & Suspension
              </button>
              <button
                className={`nav-item ${activeSection === "reminders" ? "active" : ""}`}
                onClick={() => setActiveSection("reminders")}
              >
                <Clock size={16} className="me-2" />
                Payment Reminders
              </button>
            </div>

            <div className="nav-section">
              <h5>Resources</h5>
              <button
                className={`nav-item ${activeSection === "faq" ? "active" : ""}`}
                onClick={() => setActiveSection("faq")}
              >
                <FileText size={16} className="me-2" />
                FAQ
              </button>
            </div>
          </nav>
        </div>

        {/* Content Area */}
        <div className="docs-content">
          {activeSection === "overview" && <OverviewSection />}
          {activeSection === "base-url" && <BaseURLSection copyFn={copyToClipboard} copied={copiedEndpoint} />}
          {activeSection === "endpoints" && <EndpointsSection copyFn={copyToClipboard} copied={copiedEndpoint} />}
          {activeSection === "features" && <FeaturesSection />}
          {activeSection === "late-fees" && <LateFeesSection />}
          {activeSection === "reminders" && <RemindersSection />}
          {activeSection === "faq" && <FAQSection />}
        </div>
      </div>
    </div>
  );

  return <Layout>{pageContent}</Layout>;
};

// ============ SECTIONS ============

const OverviewSection = () => (
  <div className="doc-section">
    <h2>Platform Billing Overview</h2>
    <p className="doc-intro">
      The School ERP Platform Billing system manages school subscription fees, payment processing,
      and financial reporting. Schools pay based on the number of active students, with automated
      features for reminders, late fees, and account suspension.
    </p>

    <div className="doc-card">
      <h4>Key Features</h4>
      <ul>
        <li><CheckCircle size={16} className="me-2" /> <strong>Per-Student Pricing:</strong> GHS 20 per student per academic term</li>
        <li><CheckCircle size={16} className="me-2" /> <strong>Secure Payments:</strong> Integrated with Paystack payment gateway</li>
        <li><CheckCircle size={16} className="me-2" /> <strong>Partial Payments:</strong> Schools can pay in installments</li>
        <li><CheckCircle size={16} className="me-2" /> <strong>Automatic Reminders:</strong> SMS/Email notifications before due dates</li>
        <li><CheckCircle size={16} className="me-2" /> <strong>Late Fee Management:</strong> 2.5% late fee after grace period</li>
        <li><CheckCircle size={16} className="me-2" /> <strong>Account Suspension:</strong> Automatic suspension after 14 days overdue</li>
        <li><CheckCircle size={16} className="me-2" /> <strong>Invoice Management:</strong> Digital invoices for all transactions</li>
        <li><CheckCircle size={16} className="me-2" /> <strong>Financial Reports:</strong> Revenue analytics and collection metrics</li>
      </ul>
    </div>

    <div className="doc-card">
      <h4>Payment Flow</h4>
      <div className="flow-diagram">
        <div className="flow-step">
          <span className="step-num">1</span>
          <span className="step-text">School bills for active students</span>
        </div>
        <div className="flow-arrow">→</div>
        <div className="flow-step">
          <span className="step-num">2</span>
          <span className="step-text">Invoice generated (due in 30 days)</span>
        </div>
        <div className="flow-arrow">→</div>
        <div className="flow-step">
          <span className="step-num">3</span>
          <span className="step-text">School makes payment via Paystack</span>
        </div>
        <div className="flow-arrow">→</div>
        <div className="flow-step">
          <span className="step-num">4</span>
          <span className="step-text">Invoice marked paid</span>
        </div>
      </div>
    </div>
  </div>
);




const FeaturesSection = () => (
  <div className="doc-section">
    <h2>Features & Capabilities</h2>

    <div className="feature-grid">
      <div className="feature-card">
        <div className="feature-icon"><Money size={28} weight="fill" /></div>
        <h4>Flexible Pricing</h4>
        <p>GHS 20 per student per academic term with support for partial payments</p>
      </div>

      <div className="feature-card">
        <div className="feature-icon"><Lock size={28} weight="fill" /></div>
        <h4>Secure Payments</h4>
        <p>Integrated with Paystack for secure payment processing via Mobile Money and Cards</p>
      </div>

      <div className="feature-card">
        <div className="feature-icon"><Envelope size={28} weight="fill" /></div>
        <h4>Smart Reminders</h4>
        <p>Automatic SMS/Email reminders sent at strategic intervals before payment due dates</p>
      </div>

      <div className="feature-card">
        <div className="feature-icon"><Warning size={28} weight="fill" /></div>
        <h4>Late Fee Management</h4>
        <p>Automatic 2.5% late fee applied after 7-day grace period, with manual waiver options</p>
      </div>

      <div className="feature-card">
        <div className="feature-icon"><Prohibit size={28} weight="fill" /></div>
        <h4>Auto-Suspension</h4>
        <p>Automatic account suspension after 14 days overdue for critical collections</p>
      </div>

      <div className="feature-card">
        <div className="feature-icon"><ChartBar size={28} weight="fill" /></div>
        <h4>Financial Reports</h4>
        <p>Detailed revenue analytics, collection metrics, and arrears reporting</p>
      </div>

      <div className="feature-card">
        <div className="feature-icon"><FileText size={28} weight="fill" /></div>
        <h4>Digital Invoices</h4>
        <p>Professional PDF invoices generated automatically for all subscriptions</p>
      </div>

      <div className="feature-card">
        <div className="feature-icon"><ArrowsClockwise size={28} weight="fill" /></div>
        <h4>Multi-Tenant Ready</h4>
        <p>Full isolation of school data with comprehensive access controls</p>
      </div>
    </div>
  </div>
);

const LateFeesSection = () => (
  <div className="doc-section">
    <h2>Late Fees & Suspension System</h2>

    <div className="doc-card">
      <h4>Late Fee Configuration</h4>
      <table className="config-table">
        <tbody>
          <tr>
            <td><strong>Grace Period</strong></td>
            <td>7 days after due date</td>
          </tr>
          <tr>
            <td><strong>Late Fee Rate</strong></td>
            <td>2.5% of outstanding balance</td>
          </tr>
          <tr>
            <td><strong>Suspension Period</strong></td>
            <td>14 days after due date</td>
          </tr>
          <tr>
            <td><strong>Enable Reminders</strong></td>
            <td>Yes (configurable)</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div className="doc-card">
      <h4>Payment Timeline</h4>
      <div className="timeline">
        <div className="timeline-item">
          <div className="timeline-marker">Day 0</div>
          <p><strong>Due Date</strong> - Invoice issued if not paid</p>
        </div>
        <div className="timeline-item">
          <div className="timeline-marker">Day 7</div>
          <p><strong>Grace Period Ends</strong> - Late fee warning sent</p>
        </div>
        <div className="timeline-item">
          <div className="timeline-marker">Day 8</div>
          <p><strong>Late Fee Applied</strong> - 2.5% penalty added to balance</p>
        </div>
        <div className="timeline-item">
          <div className="timeline-marker">Day 14</div>
          <p><strong>Suspension</strong> - Account access suspended automatically</p>
        </div>
      </div>
    </div>

    <div className="doc-card">
      <h4>Suspension & Reactivation</h4>
      <p>
        When a subscription is suspended, the school loses platform access including:
      </p>
      <ul>
        <li>Student management access</li>
        <li>Finance module access</li>
        <li>Assignment and attendance features</li>
        <li>Communication and reports</li>
      </ul>
      <p className="mt-3">
        <strong>Reactivation:</strong> Full access is immediately restored upon payment of outstanding balance,
        including late fees.
      </p>
    </div>

    <div className="doc-card info-card">
      <h4><Lightbulb size={18} className="me-2" /> Manual Waiver Option</h4>
      <p>
        Administrators can manually waive late fees for specific subscriptions using the endpoint:
      </p>
      <pre className="code-example">{`POST /api/billing/subscriptions/{subscription_id}/late-fee/waive
Query Parameters:
  reason=string (optional)`}</pre>
    </div>
  </div>
);

const RemindersSection = () => (
  <div className="doc-section">
    <h2>Payment Reminder System</h2>

    <div className="doc-card">
      <h4>Reminder Schedule</h4>
      <p>Automatic reminders are sent at strategic times:</p>
      <table className="config-table">
        <tbody>
          <tr>
            <td><strong>7 Days Before Due</strong></td>
            <td>Initial reminder notification</td>
          </tr>
          <tr>
            <td><strong>On Due Date</strong></td>
            <td>Payment is now due reminder</td>
          </tr>
          <tr>
            <td><strong>3 Days After Due</strong></td>
            <td>Urgent payment reminder in grace period</td>
          </tr>
          <tr>
            <td><strong>After Grace Period</strong></td>
            <td>Final warning before late fees apply</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div className="doc-card">
      <h4>Reminder Channels</h4>
      <ul>
        <li><Envelope size={16} className="me-2" /> <strong>Email:</strong> Detailed reminders with payment instructions</li>
        <li><ChatCircle size={16} className="me-2" /> <strong>SMS:</strong> Quick mobile notifications with due amount</li>
        <li><DeviceMobile size={16} className="me-2" /> <strong>Platform Dashboard:</strong> In-app notification badge</li>
      </ul>
    </div>

    <div className="doc-card">
      <h4>View Reminder History</h4>
      <p>Track all reminders sent for a subscription:</p>
    </div>
  </div>
);

const FAQSection = () => (
  <div className="doc-section">
    <h2>Frequently Asked Questions</h2>

    <div className="doc-card">
      <h5><Question size={18} className="me-2" /> What is the platform subscription fee?</h5>
      <p>
        The platform subscription fee is GHS 20 per student per academic term
        (GHS 60 per student annually for a 3-term school year). This covers
        unlimited access to all School ERP features.
      </p>
    </div>

    <div className="doc-card">
      <h5><CreditCard size={18} className="me-2" /> How do I integrate the payment gateway?</h5>
      <p>
        The platform uses Paystack for payment processing. Initialize payments
        using the `/api/billing/payments/initialize` endpoint, which returns a
        payment link. After payment, verify the transaction via the verify endpoint.
      </p>
    </div>

    <div className="doc-card">
      <h5><ArrowsClockwise size={18} className="me-2" /> Can schools make partial payments?</h5>
      <p>
        Yes! Schools can pay any amount towards their balance. Partial payments
        reduce the outstanding balance but don't clear the invoice until fully paid.
      </p>
    </div>

    <div className="doc-card">
      <h5><Warning size={18} className="me-2" /> What happens after the grace period?</h5>
      <p>
        After 7 days past the due date, a 2.5% late fee is automatically applied.
        After 14 days past due, the school's platform access is automatically suspended
        until payment is made.
      </p>
    </div>

    <div className="doc-card">
      <h5><Prohibit size={18} className="me-2" /> Can I disable account suspension?</h5>
      <p>
        Suspension is a critical feature for collection. However, you can manually
        reactivate suspended accounts via the reactivate endpoint as needed.
      </p>
    </div>

    <div className="doc-card">
      <h5><Envelope size={18} className="me-2" /> How are reminders delivered?</h5>
      <p>
        Reminders are sent via SMS and email to school administrators. Configure
        the SMS provider in your billing settings to enable SMS notifications.
      </p>
    </div>

    <div className="doc-card">
      <h5><Question size={18} className="me-2" /> Who do I contact for billing support?</h5>
      <p>
        For billing inquiries:<br/>
        <Envelope size={16} className="me-2" /> Email: info@campusio.online<br/>
        <Phone size={16} className="me-2" /> Call: +233 53 448 4781(Business hours)<br/>
        <ChatCircle size={16} className="me-2" /> Watsapp: +233 53 448 4781 <br/>
      </p>
    </div>
  </div>
);

export default BillingDocsDashboard;
